var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { NATIVE_APP_BRIDGE_CONSTANTS, NATIVE_APP_BRIDGE_ERRORS, REQ_SOURCE, } from '../constants/NativeAppBridgeConstants';
import { fireLogWithSentryBase } from '@airtel-lego/sentry';
import { getAPIRelativeUrl, isMobileApp, isNewIOSApp, setInCookie, } from '../utils/NativeAppBridgeUtils';
import { getAnalyticsParams } from '../NativeAppBridge';
const { REQUEST_PERMISSION_CALLBACK, OTP_JOURNEY_STARTED_CALLBACK } = NATIVE_APP_BRIDGE_CONSTANTS;
/**
 * @description Purpose of this service is to connect with Native app and extract data in webview for Web apps.
 * This service is singleton as any given time there should be only one instance of this service.
 */
class NativeAppBridgeClass {
    constructor() {
        /**
         * get app headers to be sent in the every xhr request
         * @param conf {any}
         * @param options {any}
         */
        this.getHeaders = (conf = {}, options = {}) => __awaiter(this, void 0, void 0, function* () {
            try {
                const isThanksApp = yield isMobileApp(true);
                if (!isThanksApp)
                    throw new Error(NATIVE_APP_BRIDGE_ERRORS.GENERIC);
                let { method } = conf;
                method = method === null || method === void 0 ? void 0 : method.toUpperCase();
                conf.method = method;
                const url = getAPIRelativeUrl(conf, options);
                const reqPayload = this.getReqPayload(conf);
                // fill state values for later use
                let result = null;
                if (!this._isNewIosApp) {
                    result = this.getHeadersOldWebView(method, url, reqPayload);
                }
                else {
                    result = yield this.getHeadersNewIOSWebView(method, url, reqPayload);
                }
                this._appAuthHeaders[url] = {};
                return result;
            }
            catch (err) {
                fireLogWithSentryBase('index.ts-->getHeaders-->error while getting headers', err);
            }
        });
        /**
         * get req payload
         * @param conf {any}
         * @returns {string}
         */
        this.getReqPayload = (conf = {}) => {
            if (!['GET', 'DELETE', 'HEAD'].includes(conf.method) && conf.data) {
                if (conf.data instanceof FormData || conf.data instanceof Blob) {
                    return '';
                }
                return JSON.stringify(conf.data);
            }
            return '';
        };
        /**
         * get headers for old way call for UIWebView method
         * @param method {any}
         * @param url {string}
         * @param reqPayload {any}
         */
        this.getHeadersOldWebView = (method, url, reqPayload) => {
            var _a;
            try {
                const appHeaders = (_a = window.MyAirtelAppReact) === null || _a === void 0 ? void 0 : _a.getHeaders(method, url, reqPayload);
                this.parseHeaders(url, appHeaders);
                return this._appAuthHeaders[url];
            }
            catch (err) {
                fireLogWithSentryBase('index.ts-->getHeadersForOldWebView-->error while getting old headers', err);
            }
        };
        /**
         * get headers for new way call for UIWebView method in case of ios only
         * @param method {any}
         * @param url {string}
         * @param reqPayload {any}
         * @returns {Promise<any>}
         */
        this.getHeadersNewIOSWebView = (method, url, reqPayload) => {
            return new Promise((resolve) => {
                var _a, _b, _c;
                const urlPath = new URL(url).pathname.replaceAll('/', '').replaceAll('-', '');
                const callbackName = `${NATIVE_APP_BRIDGE_CONSTANTS.GET_HEADERS_CALLBACK}${urlPath}`;
                const callback = (headerRes) => {
                    resolve(this.onGetHeaderSuccess(headerRes));
                };
                if (window.app) {
                    window.app[callbackName] = callback;
                }
                else {
                    window.app = {
                        [callbackName]: callback,
                    };
                }
                const payload = {
                    [NATIVE_APP_BRIDGE_CONSTANTS.EVENT_NAME]: NATIVE_APP_BRIDGE_CONSTANTS.GET_HEADERS_EVENT,
                    [NATIVE_APP_BRIDGE_CONSTANTS.INPUT_PARAMS]: {
                        //these are the actual payload which was used to call with MyAirtelAppReact.getHeaders
                        [NATIVE_APP_BRIDGE_CONSTANTS.GET_HEADERS_METHOD]: method,
                        [NATIVE_APP_BRIDGE_CONSTANTS.GET_HEADERS_URL]: url,
                        [NATIVE_APP_BRIDGE_CONSTANTS.GET_HEADERS_PARAMS]: reqPayload,
                    },
                    [NATIVE_APP_BRIDGE_CONSTANTS.CALLBACK]: callbackName,
                };
                // fallback for when callback is not called
                setTimeout(() => {
                    resolve({});
                }, 500);
                try {
                    //post an event and wait bridge to callback your method sent above in callback key
                    (_c = (_b = (_a = window.webkit) === null || _a === void 0 ? void 0 : _a.messageHandlers) === null || _b === void 0 ? void 0 : _b.triggerAppEvent) === null || _c === void 0 ? void 0 : _c.postMessage(payload);
                }
                catch (err) {
                    fireLogWithSentryBase('index.ts-->getHeadersForNewIOSApp-->error while getting new IOS headers', err);
                }
            });
        };
        /**
         * parse headers
         * @param url {string}
         * @param jsonString {any}
         */
        this.parseHeaders = (url, jsonString) => {
            let appHeaders = String(jsonString).replace(/\n/g, '');
            appHeaders = JSON.parse(appHeaders);
            this.configureHeaders(url, appHeaders);
        };
        /**
         * configure headers
         * @param url {string}
         * @param appHeaders {any}
         */
        this.configureHeaders = (url, appHeaders) => {
            this._appAuthHeaders = Object.assign(Object.assign({}, this._appAuthHeaders), { [url]: {} });
            const headers = {};
            for (const key in appHeaders) {
                if (key)
                    this._appAuthHeaders[url][key] = String(appHeaders[key]).replace(/\n/g, '');
                headers[key] = String(appHeaders[key]).replace(/\n/g, '');
            }
            this._appAuthHeaders[url].requestSrc = REQ_SOURCE;
            headers['requestSrc'] = REQ_SOURCE;
            return headers;
        };
        /**
         * create ios web view payload
         * @param eventName{any}
         * @param inputParamsObj {any}
         * @param callBackStringName {any}
         * @returns a payload obj for ios webview
         */
        this.createIOSWebViewPayload = (eventName, inputParamsObj, callBackStringName) => {
            let payload = {};
            if (eventName)
                payload[NATIVE_APP_BRIDGE_CONSTANTS.EVENT_NAME] = eventName;
            if (inputParamsObj)
                payload[NATIVE_APP_BRIDGE_CONSTANTS.INPUT_PARAMS] = inputParamsObj;
            if (callBackStringName)
                payload[NATIVE_APP_BRIDGE_CONSTANTS.CALLBACK] = callBackStringName;
            return payload;
        };
        /**
         * callback methods which should be registered here
         * @param jsonString {any}
         */
        this.onGetHeaderSuccess = (jsonString) => {
            try {
                let appHeaders = String(jsonString).replace(/\n/g, '');
                appHeaders = JSON.parse(appHeaders);
                const url = Object.keys(appHeaders)[0];
                return this.configureHeaders(url, appHeaders[url]);
            }
            catch (err) {
                fireLogWithSentryBase('index.ts-->onGetHeaderSuccess-->error while get headers success', err);
                return {};
            }
        };
        /**
         * to keep the same session id when web view changes
         */
        this.getSessionId = () => __awaiter(this, void 0, void 0, function* () {
            var _a;
            try {
                const isThanksApp = yield isMobileApp(true);
                if (!isThanksApp)
                    throw new Error(NATIVE_APP_BRIDGE_ERRORS.GENERIC);
                if (!this._isNewIosApp) {
                    const sessionId = (_a = window.MyAirtelAppReact) === null || _a === void 0 ? void 0 : _a.getSessionID();
                    this.onGetSessionIdSuccess(sessionId);
                }
                else {
                    return new Promise((resolve) => {
                        var _a, _b, _c;
                        const callback = (sessionId) => {
                            resolve(this.onGetSessionIdSuccess(sessionId));
                        };
                        const callbackName = `${NATIVE_APP_BRIDGE_CONSTANTS.GET_SESSION_ID_CALLBACK}_${Date.now()}`;
                        if (window.app) {
                            window.app[callbackName] = callback;
                        }
                        else {
                            window.app = {
                                [callbackName]: callback,
                            };
                        }
                        (_c = (_b = (_a = window.webkit) === null || _a === void 0 ? void 0 : _a.messageHandlers) === null || _b === void 0 ? void 0 : _b.triggerAppEvent) === null || _c === void 0 ? void 0 : _c.postMessage({
                            [NATIVE_APP_BRIDGE_CONSTANTS.EVENT_NAME]: NATIVE_APP_BRIDGE_CONSTANTS.GET_SESSION_ID,
                            [NATIVE_APP_BRIDGE_CONSTANTS.CALLBACK]: callbackName,
                        });
                        // fallback for when callback is not called
                        setTimeout(() => {
                            resolve('');
                        }, 500);
                    });
                }
            }
            catch (err) {
                fireLogWithSentryBase('index.ts-->getSessionId-->error while getting session id', err);
            }
        });
        /**
         * callback methods which should be registered here after getting session id
         * @param sessionId {any}
         */
        this.onGetSessionIdSuccess = (sessionId) => {
            if (sessionId) {
                const date = new Date();
                date.setTime(date.getTime() + 30 * 60 * 1000); // sessionId to be saved for 1/2hr only.
                setInCookie([
                    {
                        cname: 'cs_sid',
                        cvalue: JSON.stringify({ uuid: sessionId, userType: 'REPEAT' }),
                        cexpire: date.toUTCString(),
                    },
                ]);
                return sessionId;
            }
        };
        /**
         * on closing web view
         */
        this.close = () => __awaiter(this, void 0, void 0, function* () {
            var _b, _c, _d, _e;
            try {
                if (!isMobileApp())
                    throw new Error(NATIVE_APP_BRIDGE_ERRORS.GENERIC);
                if (!this._isNewIosApp) {
                    (_b = window.MyAirtelAppReact) === null || _b === void 0 ? void 0 : _b.close();
                }
                else {
                    (_e = (_d = (_c = window.webkit) === null || _c === void 0 ? void 0 : _c.messageHandlers) === null || _d === void 0 ? void 0 : _d.triggerAppEvent) === null || _e === void 0 ? void 0 : _e.postMessage({
                        [NATIVE_APP_BRIDGE_CONSTANTS.EVENT_NAME]: NATIVE_APP_BRIDGE_CONSTANTS.CLOSE_WEBVIEW,
                    });
                }
            }
            catch (err) {
                fireLogWithSentryBase('index.ts-->close-->error while closing web view | app', err);
            }
        });
        /**
         * to show and hide app tool bar
         */
        this.showAppToolBar = (viewToolBar) => __awaiter(this, void 0, void 0, function* () {
            var _f, _g, _h, _j;
            try {
                const isThanksApp = yield isMobileApp();
                if (!isThanksApp)
                    throw new Error(NATIVE_APP_BRIDGE_ERRORS.GENERIC);
                if (!this._isNewIosApp) {
                    (_f = window.MyAirtelAppReact) === null || _f === void 0 ? void 0 : _f.showToolbar(viewToolBar);
                }
                else {
                    (_j = (_h = (_g = window.webkit) === null || _g === void 0 ? void 0 : _g.messageHandlers) === null || _h === void 0 ? void 0 : _h.triggerAppEvent) === null || _j === void 0 ? void 0 : _j.postMessage({
                        [NATIVE_APP_BRIDGE_CONSTANTS.EVENT_NAME]: NATIVE_APP_BRIDGE_CONSTANTS.SHOW_TOOLBAR,
                        [NATIVE_APP_BRIDGE_CONSTANTS.INPUT_PARAMS]: { show: false },
                    });
                }
            }
            catch (err) {
                fireLogWithSentryBase('index.ts-->showAppToolBar-->error while showing app toolbar | app', err);
            }
        });
        /**
         * to get analytics params
         * @returns analyticsParams object {sessionId: '', visitorID: '', logggedInNumber: ''}
         */
        this.getAnalyticsParams = () => {
            var _a;
            try {
                if (!isMobileApp())
                    throw new Error(NATIVE_APP_BRIDGE_ERRORS.GENERIC);
                if (!this._isNewIosApp) {
                    const analyticsParamsJson = (_a = window.MyAirtelAppReact) === null || _a === void 0 ? void 0 : _a.getAnalyticsParams();
                    return this.onGetAnalyticsParamsJsonSuccess(analyticsParamsJson);
                }
                else {
                    return new Promise((resolve) => {
                        let isResolved = false;
                        let intervalRef;
                        const callback = (analyticsParams) => {
                            isResolved = true;
                            clearInterval(intervalRef);
                            resolve(this.onGetAnalyticsParamsJsonSuccess(analyticsParams));
                        };
                        const fireNativeEventHandler = () => {
                            var _a, _b, _c;
                            const callbackName = `${NATIVE_APP_BRIDGE_CONSTANTS.GET_ANALYTICS_PARAMS_JSON_CALLBACK}_${Date.now()}`;
                            if (window.app) {
                                window.app[callbackName] = callback;
                            }
                            else {
                                window.app = {
                                    [callbackName]: callback,
                                };
                            }
                            (_c = (_b = (_a = window.webkit) === null || _a === void 0 ? void 0 : _a.messageHandlers) === null || _b === void 0 ? void 0 : _b.triggerAppEvent) === null || _c === void 0 ? void 0 : _c.postMessage({
                                [NATIVE_APP_BRIDGE_CONSTANTS.EVENT_NAME]: NATIVE_APP_BRIDGE_CONSTANTS.GET_ANALYTICS_PARAMS_JSON,
                                [NATIVE_APP_BRIDGE_CONSTANTS.CALLBACK]: callbackName,
                            });
                        };
                        fireNativeEventHandler();
                        intervalRef = setInterval(() => {
                            if (!isResolved) {
                                fireNativeEventHandler();
                            }
                        }, 500);
                    });
                }
            }
            catch (err) {
                fireLogWithSentryBase('index.ts-->getAnalyticsParams-->error while getting logged in number | app', err);
                return {};
            }
        };
        /**
         * callback methods which should be registered here after getting analyticsParams
         * @param analyticsParams {any}
         */
        this.onGetAnalyticsParamsJsonSuccess = (analyticsParams) => {
            let parsedAnalyticsParams = {};
            if (analyticsParams) {
                parsedAnalyticsParams = JSON.parse(analyticsParams);
            }
            return parsedAnalyticsParams;
        };
        /**
         * to send moengage event
         * @param eventName {string}
         * @param jsonBody {string}
         */
        this.markMoEngageEvent = (eventName, jsonBody) => {
            var _a, _b, _c, _d;
            try {
                if (!isMobileApp())
                    throw new Error(NATIVE_APP_BRIDGE_ERRORS.GENERIC);
                if (!this._isNewIosApp) {
                    (_a = window.MyAirtelAppReact) === null || _a === void 0 ? void 0 : _a.markMoEngageEvent(eventName, jsonBody);
                }
                else {
                    (_d = (_c = (_b = window.webkit) === null || _b === void 0 ? void 0 : _b.messageHandlers) === null || _c === void 0 ? void 0 : _c.triggerAppEvent) === null || _d === void 0 ? void 0 : _d.postMessage({
                        [NATIVE_APP_BRIDGE_CONSTANTS.EVENT_NAME]: NATIVE_APP_BRIDGE_CONSTANTS.MARK_MOENGAGE_EVENT,
                    });
                }
            }
            catch (err) {
                fireLogWithSentryBase('index.ts-->markMoEngageEvent-->error while marking moengage event | app', err);
            }
        };
        /**
         * to open scanner
         * @param title {string}
         * @param callbackName {string}
         */
        this.getBarcode = (title, config, callback) => __awaiter(this, void 0, void 0, function* () {
            var _k, _l;
            try {
                const { appNumber } = (yield getAnalyticsParams()) || {};
                if (!this._isNewIosApp) {
                    window[NATIVE_APP_BRIDGE_CONSTANTS.GET_BARCODE_CALLBACK] = callback;
                    if (appNumber >= 5532) {
                        (_k = window.MyAirtelAppReact) === null || _k === void 0 ? void 0 : _k.getBarcode(title, JSON.stringify(config), NATIVE_APP_BRIDGE_CONSTANTS.GET_BARCODE_CALLBACK);
                    }
                    else {
                        (_l = window.MyAirtelAppReact) === null || _l === void 0 ? void 0 : _l.getBarcode(title, NATIVE_APP_BRIDGE_CONSTANTS.GET_BARCODE_CALLBACK);
                    }
                }
                else {
                    // register Native App Bridge because IOS native Bridge expects callback function to be exposed on window.app property
                    if (window.app) {
                        window.app[NATIVE_APP_BRIDGE_CONSTANTS.GET_BARCODE_CALLBACK] = (result) => callback(JSON.parse(result));
                    }
                    else {
                        window.app = {
                            [NATIVE_APP_BRIDGE_CONSTANTS.GET_BARCODE_CALLBACK]: callback,
                        };
                    }
                    if (appNumber >= 835) {
                        window.webkit.messageHandlers.getBarcode.postMessage(JSON.stringify({
                            title,
                            config,
                            callback: NATIVE_APP_BRIDGE_CONSTANTS.GET_BARCODE_CALLBACK,
                        }));
                    }
                    else {
                        window.webkit.messageHandlers.getBarcode.postMessage(JSON.stringify({
                            title,
                            callback: NATIVE_APP_BRIDGE_CONSTANTS.GET_BARCODE_CALLBACK,
                        }));
                    }
                }
            }
            catch (err) {
                fireLogWithSentryBase('index.ts-->getBarcode-->error in getBarcode method | app', err);
            }
        });
        /**
         * to get callback on back press
         * @param callbackName {string}
         */
        this.customBackPress = (callback) => __awaiter(this, void 0, void 0, function* () {
            var _m, _o, _p, _q;
            try {
                const { appNumber } = (yield getAnalyticsParams()) || {};
                // Android
                if (!this._isNewIosApp) {
                    window[NATIVE_APP_BRIDGE_CONSTANTS.CUSTOM_BACKPRESS_CALLBACK] = callback;
                    (_m = window === null || window === void 0 ? void 0 : window.MyAirtelAppReact) === null || _m === void 0 ? void 0 : _m.customBackPress(NATIVE_APP_BRIDGE_CONSTANTS.CUSTOM_BACKPRESS_CALLBACK, true);
                    // IOS
                }
                else if (appNumber >= 925) {
                    // register Native App Bridge because IOS native Bridge expects callback function to be exposed on window.app property
                    if (window.app) {
                        window.app[NATIVE_APP_BRIDGE_CONSTANTS.CUSTOM_BACKPRESS_CALLBACK] =
                            callback;
                    }
                    else {
                        window.app = {
                            [NATIVE_APP_BRIDGE_CONSTANTS.CUSTOM_BACKPRESS_CALLBACK]: callback,
                        };
                    }
                    (_q = (_p = (_o = window.webkit) === null || _o === void 0 ? void 0 : _o.messageHandlers) === null || _p === void 0 ? void 0 : _p.triggerAppEvent) === null || _q === void 0 ? void 0 : _q.postMessage({
                        [NATIVE_APP_BRIDGE_CONSTANTS.EVENT_NAME]: NATIVE_APP_BRIDGE_CONSTANTS.CUSTOM_BACKPRESS,
                        [NATIVE_APP_BRIDGE_CONSTANTS.CALLBACK]: NATIVE_APP_BRIDGE_CONSTANTS.CUSTOM_BACKPRESS_CALLBACK,
                    });
                }
            }
            catch (err) {
                fireLogWithSentryBase('NativeAppBridgeClass-->customBackPress-->error in customBackPress method | app', err);
            }
        });
        this.getProfileName = () => {
            return new Promise((resolve) => {
                (() => __awaiter(this, void 0, void 0, function* () {
                    var _a, _b, _c, _d;
                    const { appNumber } = (yield this.getAnalyticsParams()) || {};
                    if (isMobileApp()) {
                        if (this._isNewIosApp) {
                            if (appNumber >= 869) {
                                const callback = (profileName) => {
                                    resolve(profileName || '');
                                };
                                const callbackName = `${NATIVE_APP_BRIDGE_CONSTANTS.GET_PROFILE_NAME_CALLBACK}_${Date.now()}`;
                                if (window.app) {
                                    window.app[callbackName] = callback;
                                }
                                else {
                                    window.app = {
                                        [callbackName]: callback,
                                    };
                                }
                                //post an event and wait bridge to callback your method sent above in callback key
                                (_c = (_b = (_a = window.webkit) === null || _a === void 0 ? void 0 : _a.messageHandlers) === null || _b === void 0 ? void 0 : _b.triggerAppEvent) === null || _c === void 0 ? void 0 : _c.postMessage({
                                    [NATIVE_APP_BRIDGE_CONSTANTS.EVENT_NAME]: NATIVE_APP_BRIDGE_CONSTANTS.GET_PROFILE_NAME,
                                    [NATIVE_APP_BRIDGE_CONSTANTS.CALLBACK]: callbackName,
                                });
                                // fallback for when callback is not called
                                setTimeout(() => {
                                    resolve('');
                                }, 500);
                            }
                            else {
                                resolve('');
                            }
                        }
                        else {
                            if (appNumber >= 5571) {
                                // android
                                resolve((_d = window.MyAirtelAppReact) === null || _d === void 0 ? void 0 : _d.getProfileName());
                            }
                            else {
                                resolve('');
                            }
                        }
                    }
                    else
                        resolve('');
                }))();
            });
        };
        /**
         * to get location data
         * lat & lon value object in case of success
         * error value object in case of failure
         * @returns  object {lat: '', lon: '' , } or {error : ''}
         */
        this.fetchCurrentLocation = () => {
            return new Promise((resolve) => {
                (() => __awaiter(this, void 0, void 0, function* () {
                    var _a, _b, _c, _d, _e;
                    if (isMobileApp()) {
                        const callbackName = NATIVE_APP_BRIDGE_CONSTANTS.FETCH_CURRENT_LOCATION_CALLBACK;
                        const callback = (locationData) => {
                            // In case of ios stringfy data is returned
                            if (typeof locationData === 'string') {
                                locationData = JSON.parse(locationData);
                            }
                            resolve({
                                lat: (locationData === null || locationData === void 0 ? void 0 : locationData.Lat) || (locationData === null || locationData === void 0 ? void 0 : locationData.lat),
                                lon: (locationData === null || locationData === void 0 ? void 0 : locationData.Lon) || (locationData === null || locationData === void 0 ? void 0 : locationData.lon),
                                error: (locationData === null || locationData === void 0 ? void 0 : locationData.Error) || (locationData === null || locationData === void 0 ? void 0 : locationData.error),
                            });
                        };
                        if (this._isNewIosApp) {
                            if (window.app) {
                                window.app[callbackName] = callback;
                            }
                            else {
                                window.app = {
                                    [callbackName]: callback,
                                };
                            }
                            //post an event and wait bridge to callback your method sent above in callback key
                            (_c = (_b = (_a = window.webkit) === null || _a === void 0 ? void 0 : _a.messageHandlers) === null || _b === void 0 ? void 0 : _b.triggerAppEvent) === null || _c === void 0 ? void 0 : _c.postMessage({
                                [NATIVE_APP_BRIDGE_CONSTANTS.EVENT_NAME]: NATIVE_APP_BRIDGE_CONSTANTS.FETCH_CURRENT_LOCATION,
                                [NATIVE_APP_BRIDGE_CONSTANTS.CALLBACK]: callbackName,
                            });
                        }
                        else {
                            if ((_d = window.MyAirtelAppReact) === null || _d === void 0 ? void 0 : _d.fetchCurrentLocation) {
                                callbackName;
                                window[callbackName] = callback;
                                (_e = window.MyAirtelAppReact) === null || _e === void 0 ? void 0 : _e.fetchCurrentLocation(callbackName);
                            }
                        }
                    }
                    else {
                        resolve({});
                    }
                }))();
            });
        };
        /**
         * to send branch analytics with Data
         * @param eventName {string}
         * @param jsonBody {any}
         */
        this.sendBranchAnalytics = (eventName, eventData) => {
            var _a, _b, _c, _d;
            try {
                if (!isMobileApp())
                    throw new Error(NATIVE_APP_BRIDGE_ERRORS.GENERIC);
                if (!this._isNewIosApp) {
                    (_a = window.MyAirtelAppReact) === null || _a === void 0 ? void 0 : _a.sendBranchAnalyticsWithData(eventName, eventData);
                }
                else {
                    (_d = (_c = (_b = window.webkit) === null || _b === void 0 ? void 0 : _b.messageHandlers) === null || _c === void 0 ? void 0 : _c.triggerAppEvent) === null || _d === void 0 ? void 0 : _d.postMessage({
                        [NATIVE_APP_BRIDGE_CONSTANTS.EVENT_NAME]: NATIVE_APP_BRIDGE_CONSTANTS.SEND_BRANCH_ANALYTICS,
                        [NATIVE_APP_BRIDGE_CONSTANTS.INPUT_PARAMS]: {
                            [NATIVE_APP_BRIDGE_CONSTANTS.EVENT]: eventName,
                            [NATIVE_APP_BRIDGE_CONSTANTS.ATTRIBUTE_MAP]: eventData,
                        },
                    });
                }
            }
            catch (err) {
                fireLogWithSentryBase('index.ts-->sendBranchAnalytics-->error while sending branch events with Data | app', err);
            }
        };
        /**
         * to get call Status
         * error value object in case of failure
         * @returns {status, response} object
         */
        this.fetchCallStatus = ({ cancelTimeout = 12000 } = {}) => {
            return new Promise((resolve, reject) => {
                var _a, _b, _c, _d, _e;
                let timerId = setTimeout(() => {
                    reject({ status: 'error', response: 'Timeout' });
                }, cancelTimeout); // 2 minutes
                if (!isMobileApp()) {
                    throw new Error(NATIVE_APP_BRIDGE_ERRORS.GENERIC);
                }
                else {
                    if (this._isNewIosApp) {
                        const callStatus = (status) => {
                            switch (status.toLowerCase()) {
                                case 'dialing':
                                case 'incoming':
                                case 'connected':
                                    clearTimeout(timerId);
                                    resolve({ status: 'success', response: 'active_call' });
                                    break;
                            }
                        };
                        const iosCallbackName = NATIVE_APP_BRIDGE_CONSTANTS.FETCH_CALL_STATUS_CALLBACK;
                        if (window.app) {
                            window.app[iosCallbackName] = callStatus;
                        }
                        else {
                            window.app = {
                                [iosCallbackName]: callStatus,
                            };
                        }
                        (_c = (_b = (_a = window.webkit) === null || _a === void 0 ? void 0 : _a.messageHandlers) === null || _b === void 0 ? void 0 : _b.observeCallStatus) === null || _c === void 0 ? void 0 : _c.postMessage('');
                    }
                    else {
                        window[NATIVE_APP_BRIDGE_CONSTANTS.FETCH_CALL_STATUS_CALLBACK] = (status) => {
                            switch (status) {
                                case '1':
                                case '2':
                                    clearTimeout(timerId);
                                    resolve({ status: 'active_call', response: status });
                                    break;
                            }
                        };
                        if ((_d = window.MyAirtelAppReact) === null || _d === void 0 ? void 0 : _d.fetchCurrentCallStatus) {
                            (_e = window.MyAirtelAppReact) === null || _e === void 0 ? void 0 : _e.fetchCurrentCallStatus(NATIVE_APP_BRIDGE_CONSTANTS.FETCH_CALL_STATUS_CALLBACK);
                        }
                        else {
                            reject({
                                status: 'error',
                                response: 'fetchCurrentCallStatus not found',
                            });
                        }
                    }
                }
            });
        };
        this.requestPermissionWebview = (permission) => {
            if (!isMobileApp())
                throw new Error(NATIVE_APP_BRIDGE_ERRORS.GENERIC);
            return new Promise((resolve, reject) => {
                var _a;
                try {
                    window[REQUEST_PERMISSION_CALLBACK] = (status) => {
                        if (status) {
                            resolve(status);
                        }
                        else {
                            reject(status);
                        }
                    };
                    (_a = window.MyAirtelAppReact) === null || _a === void 0 ? void 0 : _a.requestPermission(permission, REQUEST_PERMISSION_CALLBACK);
                }
                catch (_err) {
                    throw new Error(NATIVE_APP_BRIDGE_ERRORS.REQUEST_PERMISSION_FAILURE);
                }
            });
        };
        this.readOtpWebview = () => __awaiter(this, void 0, void 0, function* () {
            var _r;
            if (!isMobileApp())
                throw new Error(NATIVE_APP_BRIDGE_ERRORS.GENERIC);
            try {
                const permission = 'android.permission.READ_SMS,android.permission.RECEIVE_SMS';
                yield this.requestPermissionWebview(permission);
                window[OTP_JOURNEY_STARTED_CALLBACK] = (otp) => {
                    if (otp) {
                        return otp;
                    }
                    else {
                        throw new Error(NATIVE_APP_BRIDGE_ERRORS.OTP_JOURNEY_STARTED_FAILURE);
                    }
                };
                (_r = window.MyAirtelAppReact) === null || _r === void 0 ? void 0 : _r.otpJourneyStarted(OTP_JOURNEY_STARTED_CALLBACK);
            }
            catch (error) {
                throw new Error(NATIVE_APP_BRIDGE_ERRORS.OTP_JOURNEY_STARTED_FAILURE);
            }
        });
        this.stopOtpListenerWebview = (timeout = 1000) => {
            var _a;
            try {
                if ((_a = window.MyAirtelAppReact) === null || _a === void 0 ? void 0 : _a.otpJourneyEnded) {
                    setTimeout(() => {
                        var _a;
                        (_a = window.MyAirtelAppReact) === null || _a === void 0 ? void 0 : _a.otpJourneyEnded();
                    }, timeout);
                }
            }
            catch (e) {
                throw new Error(NATIVE_APP_BRIDGE_ERRORS.OTP_JOURNEY_ENDED_FAILURE);
            }
        };
        this.getUserProfileName = () => {
            return new Promise((resolve) => {
                (() => __awaiter(this, void 0, void 0, function* () {
                    var _a, _b, _c, _d;
                    const { appNumber } = (yield this.getAnalyticsParams()) || {};
                    if (isMobileApp()) {
                        if (this._isNewIosApp) {
                            if (appNumber >= 869) {
                                const callback = (profileName) => {
                                    resolve(profileName || '');
                                };
                                const callbackName = `${NATIVE_APP_BRIDGE_CONSTANTS.GET_USER_PROFILE_NAME_CALLBACK}_${Date.now()}`;
                                if (window.app) {
                                    window.app[callbackName] = callback;
                                }
                                else {
                                    window.app = {
                                        [callbackName]: callback,
                                    };
                                }
                                //post an event and wait bridge to callback your method sent above in callback key
                                (_c = (_b = (_a = window.webkit) === null || _a === void 0 ? void 0 : _a.messageHandlers) === null || _b === void 0 ? void 0 : _b.triggerAppEvent) === null || _c === void 0 ? void 0 : _c.postMessage({
                                    [NATIVE_APP_BRIDGE_CONSTANTS.EVENT_NAME]: NATIVE_APP_BRIDGE_CONSTANTS.GET_USER_PROFILE_NAME,
                                    [NATIVE_APP_BRIDGE_CONSTANTS.CALLBACK]: callbackName,
                                });
                                // fallback for when callback is not called
                                setTimeout(() => {
                                    resolve('');
                                }, 500);
                            }
                            else {
                                resolve('');
                            }
                        }
                        else {
                            if (appNumber >= 5571) {
                                // android
                                resolve((_d = window.MyAirtelAppReact) === null || _d === void 0 ? void 0 : _d.getUserProfileName());
                            }
                            else {
                                resolve('');
                            }
                        }
                    }
                    else
                        resolve('');
                }))();
            });
        };
        this._appAuthHeaders = {};
        this._isNewIosApp = isNewIOSApp();
        if (!this._instance) {
            this._instance = this;
        }
    }
    get Instance() {
        return this._instance;
    }
}
export default NativeAppBridgeClass;
